import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AxsButtonComponent, AxsSpinnerComponent } from '@vanguard/axs-angular';
import { Subject, takeUntil } from 'rxjs';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';
import { environment } from 'src/environments/environment';

import { AdobeLaunchService } from '../../adobe-launch/adobe-launch.service';
import { CertificationService } from '../../shared/services/certification/certification.service';
import { GatekeeperService } from '../../shared/services/gatekeeper/gatekeeper.service';
import { SharedInfoService } from '../../shared/services/shared-info/shared-info.service';
import { AssetLineData, LineData, TaxImpact } from '../../shared/util/calculation.interface';
import { GlobalHeaderWindow, loginLayerDeeplink } from '../../shared/util/common-utils';
import { ClientReadyReportModalComponent } from '../client-ready-report-modal/client-ready-report-modal.component';
import { ClientReadyResourcesV4Component } from '../client-ready-resources-v4/client-ready-resources-v4.component';
import { ClientTaxInfoComponent } from '../client-tax-info/client-tax-info.component';
import { DisclosuresV4Component } from '../disclosures-v4/disclosures-v4.component';
import { GraphSectionV4Component } from '../graph-section-v4/graph-section-v4.component';
import { HeroV4Component } from '../hero-v4/hero-v4.component';
import { HowItWorksComponent } from '../how-it-works-v4/how-it-works-v4.component';
import { InputTabsComponent } from '../input-tabs/input-tabs.component';
import { IntroductionModalComponent } from '../introduction-modal/introduction-modal.component';
import { MethodologyComponent } from '../methodology/methodology.component';
import { MobileInputsSheetComponent } from '../mobile-inputs-sheet/mobile-inputs-sheet.component';
import { ProductCardsComponent } from '../product-cards/product-cards.component';
import { RccPdfCustomResultsComponent } from '../rcc-pdf-custom-results/rcc-pdf-custom-results';
import { RccPdfReportInfoComponent } from '../rcc-pdf-report-info/rcc-pdf-report-info.component';
import { RedesignFeedbackSurvey } from '../redesign-feedback-survey/redesign-feedback-survey';
import { RothConvDetailsComponent } from '../roth-conv-details/roth-conv-details.component';
import { SampleClientsComponent } from '../sample-clients/sample-clients.component';
import { ServiceInterruptionBannerV4Component } from '../service-interruption-banner-v4/service-interruption-banner.component';
import { SplashScreenComponent } from '../splash-screen/splash-screen.component';
import { SurveySideButtonComponent } from '../survey-side-button/survey-side-button.component';
import { TerminologySheetComponent } from '../terminology-sheet/terminology-sheet.component';
import type { ThesisType } from '../thesis/thesis.component';
import { ThesisComponent } from '../thesis/thesis.component';

interface NavBarItem {
  label: string;
  slotName: string;
  hash: string;
  buttonId: string;
  contentId: string;
}

@Component({
  selector: 'rcc-v4-root',
  standalone: true,
  imports: [
    HeroV4Component,
    HowItWorksComponent,
    SurveySideButtonComponent,
    AxsButtonComponent,
    AxsSpinnerComponent,
    CommonModule,
    ClientReadyResourcesV4Component,
    SampleClientsComponent,
    InputTabsComponent,
    IntroductionModalComponent,
    ProductCardsComponent,
    MethodologyComponent,
    DisclosuresV4Component,
    ClientTaxInfoComponent,
    ClientReadyReportModalComponent,
    RothConvDetailsComponent,
    MobileInputsSheetComponent,
    TerminologySheetComponent,
    ThesisComponent,
    GraphSectionV4Component,
    RccPdfReportInfoComponent,
    ServiceInterruptionBannerV4Component,
    RccPdfCustomResultsComponent,
    SplashScreenComponent,
    RedesignFeedbackSurvey,
  ],
  templateUrl: './rcc-v4.component.html',
  styleUrl: './rcc-v4.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'print' },
})
export class RccV4Component implements OnInit, AfterViewInit, OnDestroy {
  private unsubscribe = new Subject<void>();
  private originalOpenLogonLayer: ((target?: string) => void) | null = null;
  loading = false;
  readonly sampleReportLink =
    'https://advisors.vanguard.com/content/dam/fas/pdfs/Roth-Conversion-Calculator-Sample.pdf';

  private readonly title = 'Roth Conversion Calculator';
  public environment = environment;

  @ViewChild('terminologySheet') terminologySheet!: TerminologySheetComponent;
  @ViewChild('inputTabsComponent') inputTabsComponent!: InputTabsComponent;
  @ViewChild('interactiveChart') interactiveChart!: GraphSectionV4Component;
  @ViewChild('mobileInputsSheet') mobileInputsSheet!: MobileInputsSheetComponent;

  isMobile = window.innerWidth <= 600;
  isIPad: boolean;

  showServiceInterruptionBanner = false;
  showFeedbackSurvey: boolean = false;
  bannerContent: any = content.Data.v4_rcc_root.content.servicebanner;
  clientReadyReportContent: any = content.Data.v4_rcc_root.content.clientReadyReport;
  mobileInputsSheetContent: any = content.Data.v4_rcc_root.content.mobileInputSheet;
  sampleClientsContent: any = content.Data.v4_rcc_root.content.sampleClientsSection;
  clientInfoTaxContent = content.Data.v4_rcc_root.content.clientInfoTax;
  rothConvDetailsContent = content.Data.v4_rcc_root.content.rothConvDetails;
  appliedSampleClientTitle: string | null = null;
  hasEditedAppliedSampleClient = false;
  isClientReadyReportModalOpen = false;
  printClientName = '';
  chartImageDataUrl: string | null = null;
  navBarData: NavBarItem[] = [];
  activeNavIndex = 0;
  private navScrollDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTaggedSubpage: string | null = null;

  inputTabsData = [
    { title: '', icon: 'assets/images/Icon v2-blank.svg', activeIcon: 'assets/images/Icon v2.svg' },
    {
      title: '',
      icon: 'assets/images/roth-conv-details.svg',
      activeIcon: 'assets/images/roth-conv.svg',
    },
  ];

  // Input values from SharedInfoService
  rothConversionAmount: number = 0;
  currentFederalTaxRate: number = 0;
  currentStateTaxRate: number = 0;
  futureFederalTaxRate: number = 0;
  futureStateTaxRate: number = 0;
  yearsUntilDist: number = 0;
  howWillYouPay: string = '';
  percentOfIra: number = 0;

  // Calculated graph data
  assetGrowthData: AssetLineData | null = null;
  breakEvenData: LineData[] | null = null;
  // BXR's own figures for this run; null on the toggle-off local path.
  taxImpact: TaxImpact | null = null;

  // Thesis state - determined by calculations
  currentThesisType: ThesisType = 'profitable';
  // False while Clear all fields has left inputs blank. The thesis shows its error state and the report
  // hides, rather than describing a result the chart no longer shows.
  inputsComplete = true;

  // Certification properties
  isExternal = environment.UserLocation === 'external';
  lockedPrint = false;
  showCertModal = false;
  showCertificationSplash = false; // For Ctrl+P print interception
  certificationModalTitle = content.Data.v4_rcc_root.content.certificationModalTitle;
  private isPrintingViaButton = false;

  // eslint-disable-next-line max-params
  constructor(
    private readonly adobeLaunchService: AdobeLaunchService,
    private readonly certificationService: CertificationService,
    private readonly gatekeeperService: GatekeeperService,
    private readonly contentService: ContentService,
    public readonly clientInfo: SharedInfoService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.adobeLaunchService.initializePage(
      { ...AdobeLaunchService.pageDefaults }, //pageTitle comes from document title, set in index.html
      { toolName: 'BETR Calculator', toolCategory: 'calculator' },
    );
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const mobile = window.innerWidth <= 600;
    if (!mobile && this.isMobile) {
      this.mobileInputsSheet.closeSheet();
    }
    this.isMobile = mobile;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const sectionIds = this.navBarData.map((item) => item.hash);
    const offsets = sectionIds
      .map((id, index) => {
        const el = document.getElementById(id);
        if (!el) {
          return { index, distance: Number.POSITIVE_INFINITY };
        }
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top);
        return { index, distance };
      })
      .sort((a, b) => a.distance - b.distance);

    if (offsets.length && offsets[0].index !== this.activeNavIndex) {
      this.activeNavIndex = offsets[0].index;

      const subpageName = this.navBarData[this.activeNavIndex]?.label.toLowerCase();
      if (subpageName) {
        if (this.navScrollDebounceTimer) {
          clearTimeout(this.navScrollDebounceTimer);
        }
        this.navScrollDebounceTimer = setTimeout(() => {
          if (subpageName !== this.lastTaggedSubpage) {
            this.adobeLaunchService.subPageViewed(subpageName, 'N');
            this.lastTaggedSubpage = subpageName;
          }
          this.navScrollDebounceTimer = null;
        }, 5000);
      }
    }
  }

  initializeTabTitles(): void {
    if (this.clientInfoTaxContent?.title) {
      this.inputTabsData[0].title = this.clientInfoTaxContent.title;
    }

    if (this.rothConvDetailsContent) {
      this.inputTabsData[1].title =
        this.rothConvDetailsContent.rothConvHeader || 'Roth conversion details';
    }
  }

  /**
   * Check if the advisor is signed in (certified)
   * Internal users are always certified; external users need to be checked
   */
  async checkCertificationStatus(): Promise<void> {
    const isCertified = await this.certificationService.updateCertificationStatus();
    this.lockedPrint = !isCertified;
  }

  // eslint-disable-next-line max-statements
  async ngOnInit(): Promise<void> {
    this.isIPad = (() => {
      const ua = navigator.userAgent;
      return (
        /iPad/.test(ua) ||
        (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ||
        (/MacIntel/.test(ua) && navigator.maxTouchPoints > 1)
      );
    })();
    // Initialize SharedInfoService with default values
    await this.clientInfo.initializeService();

    // Check certification status for external users
    await this.checkCertificationStatus();

    // Subscribe to certification status changes (e.g., when user signs in via cert modal)
    this.certificationService.certificationStatusChanged$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((isCertified: boolean) => {
        this.lockedPrint = !isCertified;
        this.cdr.detectChanges();
      });

    // Masks only the thesis + chart while a slow tax-rates fetch or chart calculation is
    // outstanding, so no one reads a stale figure. The inputs stay usable.
    this.clientInfo
      .getLoading$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((loading: boolean) => {
        this.loading = loading;
        this.cdr.detectChanges();
      });

    this.initializeContent();

    this.showServiceInterruptionBanner =
      this.gatekeeperService.getToggleValue('service_interruption');
    this.showFeedbackSurvey = this.gatekeeperService.getToggleValue('REDESIGN_FEEDBACK_SURVEY');

    await this.certificationService.updateCertificationStatus();
    this.lockedPrint = !this.certificationService.isSessionCertified;

    // Subscribe to all input values
    this.subscribeToInputs();

    // Subscribe to calculated graph data
    this.subscribeToGraphData();

    // Subscribe to input validity changes
    this.clientInfo
      .getInputsValid$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.updateThesisType();
      });

    this.clientInfo
      .getInputsComplete$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((complete) => {
        this.inputsComplete = complete;
        this.cdr.detectChanges();
      });

    // this.adobeLaunchService.pageLoadCompleted();
  }

  private initializeContent(): void {
    // Derive from the seeded fallback first so the tabs and nav bar render before AEM responds.
    this.applyContentDerivedState(content.Data.v4_rcc_root.content);

    this.contentService.getContent().subscribe((data) => {
      const rccContent = data.Data.v4_rcc_root.content;

      this.bannerContent = rccContent.servicebanner;
      this.clientReadyReportContent = rccContent.clientReadyReport;
      this.mobileInputsSheetContent = rccContent.mobileInputSheet;
      this.sampleClientsContent = rccContent.sampleClientsSection;
      this.clientInfoTaxContent = rccContent.clientInfoTax;
      this.rothConvDetailsContent = rccContent.rothConvDetails;
      this.certificationModalTitle =
        rccContent.certificationModalTitle ?? this.certificationModalTitle;

      this.applyContentDerivedState(rccContent);
      this.cdr.detectChanges();
    });
  }

  /** Derived state — must be recomputed whenever the content it reads changes. */
  private applyContentDerivedState(rccContent: any): void {
    this.initializeTabTitles();
    this.navBarData = this.createNavBarData(this.getNavLabels(rccContent));
  }

  private getNavLabels(rccContent: any): string[] {
    if (Array.isArray(rccContent.nav?.items) && rccContent.nav.items.length) {
      return rccContent.nav.items;
    }

    const navBarContent = rccContent.navBar;
    if (!navBarContent || typeof navBarContent !== 'object') {
      return [];
    }

    const orderedKeys = [
      'overview',
      'sampleClients',
      'calculator',
      'clientReadyResources',
      'methodology',
    ];
    return orderedKeys
      .map((key) => navBarContent[key])
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  }

  private subscribeToInputs(): void {
    this.clientInfo
      .getRothConversionAmount$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.rothConversionAmount = value;
      });

    this.clientInfo
      .getCurrentFederalTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.currentFederalTaxRate = value;
      });

    this.clientInfo
      .getCurrentStateTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.currentStateTaxRate = value;
      });

    this.clientInfo
      .getFutureFederalTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.futureFederalTaxRate = value;
      });

    this.clientInfo
      .getFutureStateTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.futureStateTaxRate = value;
      });

    this.clientInfo
      .getYearsUntilDist$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.yearsUntilDist = value;
      });

    this.clientInfo
      .getHowWillYouPay$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.howWillYouPay = value;
      });

    this.clientInfo
      .getPercentOfIra$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.percentOfIra = value ?? 0;
      });

    this.clientInfo
      .getSampleClientIndex$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((index) => {
        this.appliedSampleClientTitle = this.getAppliedSampleClientTitle(index);
      });

    this.clientInfo
      .getSampleClientLoaded$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.hasEditedAppliedSampleClient = false;
      });

    this.clientInfo
      .getSampleClientModified$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((modified) => {
        this.hasEditedAppliedSampleClient = modified;
      });
  }

  get showAppliedSampleClientBanner(): boolean {
    return Boolean(this.appliedSampleClientTitle) && !this.hasEditedAppliedSampleClient;
  }

  private getAppliedSampleClientTitle(index: number): string | null {
    if (index <= 0) {
      return null;
    }

    const sampleClientTitle =
      this.sampleClientsContent?.sampleClientModals?.data?.[index - 1]?.title;
    return typeof sampleClientTitle === 'string' && sampleClientTitle.trim().length > 0
      ? sampleClientTitle
      : null;
  }

  private subscribeToGraphData(): void {
    this.clientInfo
      .getGraphData$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((graphData) => {
        this.assetGrowthData = graphData.assetGrowthData;
        this.breakEvenData = graphData.breakEvenData;
        this.taxImpact = graphData.taxImpact ?? null;

        // Update thesis type based on calculations
        this.updateThesisType();
        this.cdr.detectChanges();
      });
  }

  createNavBarData(labels: string[]): NavBarItem[] {
    return labels.map((label, index) => {
      const slug = label.toLowerCase().replace(/ /g, '-');
      return {
        label,
        slotName: slug,
        hash: slug,
        buttonId: `tab-${index}`,
        contentId: `content-${index}`,
      };
    });
  }

  onNavClick(index: number, hash: string): void {
    this.activeNavIndex = index;

    const subpageName = this.navBarData[index]?.label.toLowerCase();
    if (subpageName && subpageName !== this.lastTaggedSubpage) {
      this.adobeLaunchService.subPageViewed(subpageName, 'Y');
      this.lastTaggedSubpage = subpageName;
    }

    const target = document.getElementById(hash);
    if (target) {
      const navHeight = document.querySelector('.rcc-nav')?.getBoundingClientRect().height ?? 0;
      const top = target.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    if (hash) {
      window.history.replaceState(null, '', `#${hash}`);
    }
  }

  scrollToCalculatorSection(): void {
    this.scrollToElementById('calculator');
  }

  scrollToSampleClientsSection(): void {
    this.scrollToElementById('sample-clients');
  }

  openTerminologySheet() {
    this.terminologySheet.openSheet();
  }

  // Calculate current tax rate (combined federal + state)
  get currentTaxRate(): number {
    return (this.currentFederalTaxRate + this.currentStateTaxRate) / 100;
  }

  // The future rate the chart was actually run with. From BXR that's its own derived rate (or the
  // advisor's override), which can differ from the form; comparing the form's rate against BXR's
  // break-even rate would let the thesis contradict the chart.
  get futureTaxRate(): number {
    return (
      this.taxImpact?.taxRateDerivationDetails.futureIncomeTaxRate ??
      (this.futureFederalTaxRate + this.futureStateTaxRate) / 100
    );
  }

  // Tax owed on the conversion. BXR computes it per bracket (conversion stacked on income, IRA
  // flag applied for state); the formula is only the toggle-off local path's figure.
  get rothConversionTaxAmount(): string {
    const taxAmount =
      this.taxImpact?.incomeTaxBracketCalculationDetails.conversionTaxes ??
      this.rothConversionAmount * this.currentTaxRate * (1 - this.percentOfIra / 100);
    return '$' + Math.round(taxAmount).toLocaleString('en-US');
  }

  // Calculate asset difference (gain or loss) at distribution year
  get assetDifference(): string {
    if (!this.assetGrowthData || !this.yearsUntilDist) return '$0';

    const withConversion = this.assetGrowthData.conversionLineData[this.yearsUntilDist]?.value ?? 0;
    const withoutConversion =
      this.assetGrowthData.nonConversionLineData[this.yearsUntilDist]?.value ?? 0;
    const difference = Math.abs(withConversion - withoutConversion);

    return '$' + Math.round(difference).toLocaleString('en-US');
  }

  // Calculate BETR (Break-Even Tax Rate) at distribution year
  get betrValue(): string {
    if (!this.breakEvenData || !this.yearsUntilDist) return '0%';

    const taxRateAtDistYear =
      this.breakEvenData.find((data) => data.year === this.yearsUntilDist)?.value ?? 0;

    const betr = Math.round((taxRateAtDistYear + Number.EPSILON) * 100);
    return betr + '%';
  }

  // Calculate desirability differential to determine thesis type
  get rothDesirabilityDifferential(): number {
    if (!this.breakEvenData || !this.yearsUntilDist) return 0;

    const valueAtDistYear =
      this.breakEvenData.find((data) => data.year === this.yearsUntilDist)?.value ?? 0;

    const futureTaxRate = Math.round((this.futureTaxRate + Number.EPSILON) * 10000) / 100;
    const betr = Math.round((valueAtDistYear + Number.EPSILON) * 10000) / 100;

    return futureTaxRate - betr;
  }

  // Get payment method formatted for thesis
  get paymentMethod(): string {
    const method = this.howWillYouPay?.toLowerCase() || '';

    if (method.includes('cash')) return 'cash';
    if (method.includes('ira')) return 'ira';
    if (method.includes('tax-inefficient')) return 'tax_inefficient';
    if (method.includes('tax-efficient')) return 'tax_efficient';

    return 'cash';
  }

  // Update thesis type based on calculation results
  private updateThesisType(): void {
    // Check for input validation errors FIRST
    if (!this.clientInfo.inputsValid) {
      this.currentThesisType = 'error';
      return;
    }

    // Wait for data before evaluating thesis; do not force error state here.
    if (!this.assetGrowthData || !this.breakEvenData || !this.yearsUntilDist) {
      return;
    }

    const differential = this.rothDesirabilityDifferential;

    if (differential > 0) {
      this.currentThesisType = 'profitable';
    } else if (differential < 0) {
      this.currentThesisType = 'loss';
    } else {
      this.currentThesisType = 'no-difference';
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.isExternal) {
      await this.resumeReportAfterLogin();
    }
  }

  ngOnDestroy(): void {
    this.restoreOpenLogonLayer();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * The cert modal's Login link brings the advisor back with ?fromLogin=true. The scenario is restored
   * whether or not the login took, so the inputs aren't lost; the report only unlocks if it did.
   */
  private async resumeReportAfterLogin(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('fromLogin') !== 'true') {
      return;
    }

    // Stripped before the status check, so a refresh can't replay the flow.
    urlParams.delete('fromLogin');
    const query = urlParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );

    this.clientInfo.restoreScenarioAfterLogin();

    if (await this.certificationService.checkLoginStatus()) {
      await this.onCertificationSuccess();
    }
  }

  onRedirectedToSignIn(): void {
    this.clientInfo.saveScenarioForLogin();
    // cert-layer's own call has already hit the no-op by now; put the real one back so the deeplink
    // reaches the global header.
    this.restoreOpenLogonLayer();
    loginLayerDeeplink(`${window.location.pathname}?fromLogin=true`);
  }

  /**
   * cert-layer's Login button calls openLogonLayer() itself, with no target, before emitting
   * redirected-to-sign-in. That call would send the advisor to sign-in without the ?fromLogin return
   * path, so it is a no-op for as long as the modal is open.
   */
  private suppressOpenLogonLayer(): void {
    const globalHeader = (window as GlobalHeaderWindow).notifyGlobalHeader;
    if (globalHeader?.openLogonLayer && !this.originalOpenLogonLayer) {
      this.originalOpenLogonLayer = globalHeader.openLogonLayer;
      globalHeader.openLogonLayer = () => {};
    }
  }

  private restoreOpenLogonLayer(): void {
    const globalHeader = (window as GlobalHeaderWindow).notifyGlobalHeader;
    if (globalHeader && this.originalOpenLogonLayer) {
      globalHeader.openLogonLayer = this.originalOpenLogonLayer;
    }
    this.originalOpenLogonLayer = null;
  }

  onPrepareReportClick(): void {
    if (this.requiresCertification()) {
      this.showCertModal = true;
      this.suppressOpenLogonLayer();
    } else {
      this.openClientReadyReportModal();
    }
    this.adobeLaunchService.captureCtaButton('chart:print-client-ready-report', 'betr calculator');
  }

  openClientReadyReportModal() {
    this.isClientReadyReportModalOpen = true;
  }

  updateLockedPrintStatus() {
    this.lockedPrint = !this.certificationService.isSessionCertified;
  }

  async onCertModalClosed(): Promise<void> {
    this.showCertModal = false;
    this.restoreOpenLogonLayer();
    this.updateLockedPrintStatus();
  }

  async onCertificationSuccess(): Promise<void> {
    this.showCertModal = false;
    this.restoreOpenLogonLayer();

    this.certificationService.notifyCertificationSuccess();
    this.updateLockedPrintStatus();

    setTimeout(() => {
      this.openClientReadyReportModal();
    }, 300);
  }

  onDownloadClientReadyReport(clientName: string = ''): void {
    this.printClientName = clientName;

    this.isClientReadyReportModalOpen = false;

    if (this.requiresCertification()) {
      this.showCertModal = true;
      this.suppressOpenLogonLayer();
    } else {
      this.executePdfDownload(clientName);
    }
  }

  private executePdfDownload(clientName: string = ''): void {
    const ctaName = clientName
      ? 'chart:download-client-ready-report-customization'
      : 'chart:download-client-ready-report-no-customization';
    this.adobeLaunchService.captureCtaButton(ctaName);
    this.adobeLaunchService.captureAction('task complete', 'button', 'betr calculator');
    this.isPrintingViaButton = true;
    this.getChartForPrint();
    setTimeout(() => {
      document.execCommand('print');
      this.isPrintingViaButton = false;
    }, 100);
  }

  private getChartForPrint(): void {
    // Capture chart as static image for the print page
    this.chartImageDataUrl = this.interactiveChart?.getChartImageDataUrl() ?? null;
  }

  private requiresCertification(): boolean {
    return this.isExternal && !this.certificationService.isSessionCertified;
  }

  @HostListener('window:beforeprint')
  onBeforePrint(): void {
    if (this.requiresCertification()) {
      this.showCertificationSplash = true;
      this.cdr.detectChanges();
      this.adobeLaunchService.captureAction('print-estimated-costs-unauthorized', 'browser');
    } else {
      this.showCertificationSplash = false;
      document.title = 'Roth IRA Conversion Report';
      if (this.isPrintingViaButton) return;
      this.getChartForPrint();
      this.adobeLaunchService.captureAction('print', 'browser');
    }
  }

  @HostListener('window:afterprint')
  afterPrint(): void {
    this.showCertificationSplash = false;
    document.title = this.title;
  }

  onInfoIconClicked(event: { accordion: string; fieldId: string; highlightOverride?: string }) {
    this.terminologySheet.openWithHighlight(
      event.accordion as 'clientTax' | 'rothConversion' | 'profitabilityPoint' | 'breakEvenTaxRate',
      event.fieldId,
      event.highlightOverride,
    );
  }

  openSheetToBETR() {
    this.terminologySheet.openWithHighlight('breakEvenTaxRate', 'break-even-tax-rate-definition');
  }

  openMobileInputsSheet(): void {
    this.mobileInputsSheet.openSheet();
  }

  onMobileInputsUpdate(): void {
    this.scrollToElementById('calculator');
  }

  openSheetToCalculatorResults() {
    this.terminologySheet.openWithHighlight(null, 'tax-considerations-content');
  }

  onClientSelected() {
    this.inputTabsComponent.goToTab(0);
    this.scrollToCalculatorSection();
  }

  adobeTagSampleReport(): void {
    this.adobeLaunchService.captureAction('view-sample-client-ready-report', 'link');
  }

  private scrollToElementById(elementId: string): void {
    const target = document.getElementById(elementId);
    if (!target) {
      return;
    }

    const navHeight = document.querySelector('.rcc-nav')?.getBoundingClientRect().height ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - navHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
